import { baseApi } from "./base-api";
import dashboardApi from "./dashboard-api";
import { ApiEndpoints, buildEndpoint } from "@_constants/endpoints";
import type { CreateWidgetRequest, WidgetResponse } from "@/models/Widget";
import type { AppDispatch, RootState } from "@store/index";
import { syncWidgetDatasetUsage } from "./widget-dataset-usage-sync";
import { parseContentDispositionFilename } from "@/utils/download-file";

export interface ExportWidgetResult {
  blob: Blob;
  filename: string | null;
}

// Reads a widget from the cached tab layout — used to recover a widget's
// datasetId / config for base-usage reconciliation when the request body
// doesn't carry them (e.g. deletes, position-only updates).
function getCachedWidget(
  getState: () => RootState,
  dashboardId: string,
  tabId: string,
  widgetId: string,
): WidgetResponse | undefined {
  const layout = dashboardApi.endpoints.getTabLayout.select({
    dashboardId,
    tabId,
  })(getState());
  return layout.data?.widgets?.find((w) => w.id === widgetId);
}

export const widgetApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    createWidget: builder.mutation<
      WidgetResponse,
      { dashboardId: string; tabId: string; body: CreateWidgetRequest }
    >({
      query: ({ dashboardId, tabId, body }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGETS, { dashboardId, tabId }),
        method: "POST",
        body,
      }),
      // No invalidatesTags — cache is updated after API response via onQueryStarted
      async onQueryStarted(
        { dashboardId, tabId, body },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          // Wait for the API response to get the server-generated widget ID
          const { data: newWidget } = await queryFulfilled;
          // Append the new widget to the getTabLayout cache
          dispatch(
            dashboardApi.util.updateQueryData(
              "getTabLayout",
              { dashboardId, tabId },
              (draft) => {
                if (draft.widgets) {
                  draft.widgets.push(newWidget);
                } else {
                  draft.widgets = [newWidget];
                }
              },
            ),
          );
          await syncWidgetDatasetUsage(
            dispatch as AppDispatch,
            getState as () => RootState,
            body.datasetId || newWidget.datasetId,
            newWidget.id,
            dashboardId,
            body.configJson ?? newWidget.configJson,
          );
        } catch {
          // If creation fails, no cache update needed
        }
      },
    }),
    updateWidget: builder.mutation<
      WidgetResponse,
      {
        dashboardId: string;
        tabId: string;
        widgetId: string;
        body: Partial<CreateWidgetRequest>;
      }
    >({
      query: ({ dashboardId, tabId, widgetId, body }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_BY_ID, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "PATCH",
        body,
      }),
      // No invalidatesTags — optimistic cache update via onQueryStarted
      // Only the specific changed widget triggers a search, not all widgets
      async onQueryStarted(
        { dashboardId, tabId, widgetId, body },
        { dispatch, queryFulfilled, getState },
      ) {
        // Capture the widget's dataset before the optimistic patch, so a
        // dataset change still reconciles usage against the previous dataset.
        const priorDatasetId = getCachedWidget(
          getState as () => RootState,
          dashboardId,
          tabId,
          widgetId,
        )?.datasetId;
        // Optimistically update the getTabLayout cache with new widget data
        const patchResult = dispatch(
          dashboardApi.util.updateQueryData(
            "getTabLayout",
            { dashboardId, tabId },
            (draft) => {
              const widget = draft.widgets?.find((w) => w.id === widgetId);
              if (widget) {
                if (body.configJson !== undefined)
                  widget.configJson = body.configJson;
                if (body.xPosition !== undefined)
                  widget.xPosition = body.xPosition;
                if (body.yPosition !== undefined)
                  widget.yPosition = body.yPosition;
                if (body.width !== undefined) widget.width = body.width;
                if (body.height !== undefined) widget.height = body.height;
                if (body.datasetId !== undefined)
                  widget.datasetId = body.datasetId;
                if (body.questionId !== undefined)
                  widget.questionId = body.questionId;
                if (body.datasetVersionId !== undefined)
                  widget.datasetVersionId = body.datasetVersionId;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          return;
        }

        // Position-only updates carry no configJson and can't change base/metric usage.
        if (body.configJson === undefined) return;
        await syncWidgetDatasetUsage(
          dispatch as AppDispatch,
          getState as () => RootState,
          body.datasetId ?? priorDatasetId,
          widgetId,
          dashboardId,
          body.configJson,
        );
      },
    }),
    deleteWidget: builder.mutation<
      void,
      { dashboardId: string; tabId: string; widgetId: string }
    >({
      query: ({ dashboardId, tabId, widgetId }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_BY_ID, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "DELETE",
      }),
      // Keep the getTabLayout cache in sync so stale widgets don't reappear
      // if the tab unmounts/remounts (e.g. during a rename) before the cache expires.
      async onQueryStarted(
        { dashboardId, tabId, widgetId },
        { dispatch, queryFulfilled, getState },
      ) {
        // Capture the dataset before removal so we can drop the widget from its base.
        const datasetId = getCachedWidget(
          getState as () => RootState,
          dashboardId,
          tabId,
          widgetId,
        )?.datasetId;
        const patchResult = dispatch(
          dashboardApi.util.updateQueryData(
            "getTabLayout",
            { dashboardId, tabId },
            (draft) => {
              if (draft.widgets) {
                const index = draft.widgets.findIndex((w) => w.id === widgetId);
                if (index !== -1) {
                  draft.widgets.splice(index, 1);
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          return;
        }
        // No configJson removes the widget from whichever base/metrics referenced it.
        await syncWidgetDatasetUsage(
          dispatch as AppDispatch,
          getState as () => RootState,
          datasetId,
          widgetId,
          dashboardId,
          null,
        );
      },
    }),
    duplicateWidget: builder.mutation<
      WidgetResponse,
      {
        dashboardId: string;
        tabId: string;
        widgetId: string;
        /** Omit to duplicate into the same tab (existing one-click Duplicate behavior). */
        targetTabId?: string;
      }
    >({
      query: ({ dashboardId, tabId, widgetId, targetTabId }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_DUPLICATE, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "POST",
        body: targetTabId ? { targetTabId } : undefined,
      }),
      // Use optimistic cache update instead of invalidatesTags to prevent flickering
      // The old approach (invalidatesTags) caused a full refetch which briefly showed
      // stale cached data before loading the fresh data
      async onQueryStarted(
        { dashboardId, tabId, targetTabId },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          // Wait for the API response to get the new widget with server-generated ID
          const { data: duplicatedWidget } = await queryFulfilled;

          // Append the duplicated widget to the destination tab's getTabLayout cache
          // (same tab as before when no targetTabId was given)
          dispatch(
            dashboardApi.util.updateQueryData(
              "getTabLayout",
              { dashboardId, tabId: targetTabId ?? tabId },
              (draft) => {
                if (draft.widgets) {
                  draft.widgets.push(duplicatedWidget);
                } else {
                  draft.widgets = [duplicatedWidget];
                }
              },
            ),
          );
          // A duplicated widget stays linked to the same base/metrics as its source.
          await syncWidgetDatasetUsage(
            dispatch as AppDispatch,
            getState as () => RootState,
            duplicatedWidget.datasetId,
            duplicatedWidget.id,
            dashboardId,
            duplicatedWidget.configJson,
          );
        } catch {
          // If duplication fails, no cache update needed
        }
      },
    }),
    moveWidget: builder.mutation<
      WidgetResponse,
      {
        dashboardId: string;
        /** Widget's current tab. */
        tabId: string;
        widgetId: string;
        targetTabId: string;
      }
    >({
      query: ({ dashboardId, tabId, widgetId, targetTabId }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_MOVE, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "POST",
        body: { targetTabId },
      }),
      // Only patch caches after a successful move — a failed move must leave the
      // source tab untouched and create no destination widget.
      async onQueryStarted(
        { dashboardId, tabId, widgetId, targetTabId },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data: movedWidget } = await queryFulfilled;

          dispatch(
            dashboardApi.util.updateQueryData(
              "getTabLayout",
              { dashboardId, tabId },
              (draft) => {
                if (!draft.widgets) return;
                const index = draft.widgets.findIndex(
                  (w) => w.id === widgetId,
                );
                if (index !== -1) draft.widgets.splice(index, 1);
              },
            ),
          );
          dispatch(
            dashboardApi.util.updateQueryData(
              "getTabLayout",
              { dashboardId, tabId: targetTabId },
              (draft) => {
                if (draft.widgets) {
                  draft.widgets.push(movedWidget);
                } else {
                  draft.widgets = [movedWidget];
                }
              },
            ),
          );
          // Base/metric usage is tracked per dashboard/widget, not per tab — the
          // widget keeps its existing base/metric links after moving tabs.
          await syncWidgetDatasetUsage(
            dispatch as AppDispatch,
            getState as () => RootState,
            movedWidget.datasetId,
            movedWidget.id,
            dashboardId,
            movedWidget.configJson,
          );
        } catch {
          // If the move fails, no cache update needed
        }
      },
    }),
    uploadWidgetImage: builder.mutation<
      WidgetResponse,
      {
        dashboardId: string;
        tabId: string;
        widgetId: string;
        file: File;
        altText: string;
      }
    >({
      query: ({ dashboardId, tabId, widgetId, file, altText }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("altText", altText);
        return {
          url: buildEndpoint(ApiEndpoints.TAB_WIDGET_IMAGE, {
            dashboardId,
            tabId,
            widgetId,
          }),
          method: "POST",
          body: formData,
        };
      },
      async onQueryStarted(
        { dashboardId, tabId, widgetId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedWidget } = await queryFulfilled;
          dispatch(
            dashboardApi.util.updateQueryData(
              "getTabLayout",
              { dashboardId, tabId },
              (draft) => {
                const widget = draft.widgets?.find((w) => w.id === widgetId);
                if (widget) {
                  widget.configJson = updatedWidget.configJson;
                }
              },
            ),
          );
        } catch {
          // Errors are surfaced to the caller via unwrap()
        }
      },
      invalidatesTags: (_result, _error, { widgetId }) => [
        { type: "WidgetImage", id: widgetId },
      ],
    }),
    getWidgetImage: builder.query<
      Blob,
      { dashboardId: string; tabId: string; widgetId: string }
    >({
      query: ({ dashboardId, tabId, widgetId }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_IMAGE, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
      providesTags: (_result, _error, { widgetId }) => [
        { type: "WidgetImage", id: widgetId },
      ],
    }),
    exportWidget: builder.mutation<
      ExportWidgetResult,
      {
        dashboardId: string;
        tabId: string;
        widgetId: string;
        format: "csv" | "xlsx";
      }
    >({
      query: ({ dashboardId, tabId, widgetId, format }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_EXPORT, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "GET",
        params: { format },
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            try {
              return await response.json();
            } catch {
              return undefined;
            }
          }
          return {
            blob: await response.blob(),
            filename: parseContentDispositionFilename(
              response.headers.get("Content-Disposition"),
            ),
          };
        },
      }),
    }),
    reorderWidget: builder.mutation<
      void,
      {
        dashboardId: string;
        tabId: string;
        widgetId: string;
        newOrderIndex: number;
      }
    >({
      query: ({ dashboardId, tabId, widgetId, newOrderIndex }) => ({
        url: buildEndpoint(ApiEndpoints.TAB_WIDGET_REORDER, {
          dashboardId,
          tabId,
          widgetId,
        }),
        method: "PUT",
        body: { newOrderIndex },
      }),
      // No invalidatesTags — keep cache in sync without refetching
      async onQueryStarted(
        { dashboardId, tabId, widgetId, newOrderIndex },
        { dispatch, queryFulfilled },
      ) {
        // Optimistically reorder the widgets array in cache to match the new order
        // newOrderIndex is 1-based (for the API), convert to 0-based for array operations
        const zeroBased = newOrderIndex - 1;
        const patchResult = dispatch(
          dashboardApi.util.updateQueryData(
            "getTabLayout",
            { dashboardId, tabId },
            (draft) => {
              if (!draft.widgets) return;
              const oldIndex = draft.widgets.findIndex(
                (w) => w.id === widgetId,
              );
              if (oldIndex === -1) return;
              const [moved] = draft.widgets.splice(oldIndex, 1);
              draft.widgets.splice(zeroBased, 0, moved);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useCreateWidgetMutation,
  useUpdateWidgetMutation,
  useDeleteWidgetMutation,
  useDuplicateWidgetMutation,
  useMoveWidgetMutation,
  useReorderWidgetMutation,
  useUploadWidgetImageMutation,
  useGetWidgetImageQuery,
  useExportWidgetMutation,
} = widgetApi;

export default widgetApi;
